/**
 * Frame Selector Module
 * Handles frame selection, preview updates, and pricing
 */
(function () {
  'use strict';

  // Site-wide defaults applied on every fresh PDP load (client request 2026-05-29).
  // No persistence — every PDP loads with these defaults regardless of prior pick.
  // Fall back gracefully if removed from frames.json / background-colors.json.
  const DEFAULT_FRAME_VALUE = 'solid_wood_frame_5140hk';
  const DEFAULT_WALL_COLOR = '#B4BFA4';

  const FrameSelector = {
    // DOM Elements
    elements: {
      selector: null,
      swatches: null,
      framingOptions: null,
      previewImage: null,
      previewContainer: null,
      priceDisplay: null,
      sizeInput: null,
      tabs: null,
      tabButtons: null,
      colorSwatches: null,
      // Cart inputs
      cartFrameStyle: null,
      cartFramingOption: null,
      cartFramePrice: null,
      cartTotalPrice: null,
      // Frame preview elements
      frameOverlay: null,
      framePieces: null,
      previewWrapper: null,
    },

    // State
    state: {
      selectedSize: '',
      selectedFrame: '',
      selectedFramingOption: 'option_1',
      selectedBackgroundColor: '#E5DCD0', // on-palette fallback; overridden by palette[0] after load
      framePreviewData: {},
      basePrice: 0,
      framesData: [],
      backgroundColors: [],
      currentTab: 'popular',
      categorizedFrames: {
        popular: [],
        more: [],
        floater: []
      }
    },

    init() {
      this.cacheElements();
      if (!this.elements.selector) return;

      this.state.framePreviewData = window.framePreviewData || {};
      this.state.basePrice = window.baseProductPrice || 0;
      this.state.selectedSize = this.elements.sizeInput?.value || '';

      this.loadFramesData().then(() => {
        this.loadBackgroundColors().then(() => {
          this.bindEvents();
          this.applyDefaultFrame();
          this.updateUI();
        });
      });
    },

    applyDefaultFrame() {
      const target = this.state.framesData.find(f => f.value === DEFAULT_FRAME_VALUE);
      if (!target) return; // graceful fallback: leave selectedFrame empty
      // selectFrame handles state + DOM active class + framing-options visibility
      // + summary mirror + updateUI. Safe to call here after swatches are rendered.
      this.selectFrame(target.value, target.title);
    },

    async loadFramesData() {
      try {
        const framesUrl = window.framesJsonUrl || '/assets/frames.json';
        const response = await fetch(framesUrl + '?t=' + Date.now()); // Cache bust
        const rawData = await response.json();

        // Process image URLs to use Shopify asset URLs
        this.state.framesData = rawData.map(frame => ({
          ...frame,
          image_url: this.getAssetUrl(frame.image_url)
        }));

        // Debug: show floater frames data
        const floaters = this.state.framesData.filter(f =>
          f.value?.toLowerCase().includes('floater') || f.title?.toLowerCase().includes('floater')
        );
        console.log('Loaded floater frames:', floaters.slice(0, 3));

        this.categorizeFrames();
        this.renderFrameSwatches();
      } catch (error) {
        console.error('Error loading frames data:', error);
        this.state.framesData = [];
      }
    },

    /**
     * Get full Shopify asset URL from filename
     */
    getAssetUrl(filename) {
      if (!filename) return '';

      // If already a full URL, return as-is
      if (filename.startsWith('http://') || filename.startsWith('https://')) {
        return filename;
      }

      // Construct Shopify asset URL
      // Use the base URL from window.frameAssetsBaseUrl or construct from current domain
      const baseUrl = window.frameAssetsBaseUrl || '';
      if (baseUrl) {
        // Extract the base part (everything before 'nexo-img-')
        const basePart = baseUrl.split('nexo-img-')[0];
        return basePart + filename;
      }

      // Fallback: construct URL manually
      // This assumes Shopify asset URL pattern
      return `/cdn/shop/t/*/assets/${filename}`;
    },


    async loadBackgroundColors() {
      try {
        const colorsUrl = window.backgroundColorsUrl || '/assets/background-colors.json';
        const response = await fetch(colorsUrl);
        this.state.backgroundColors = await response.json();
        // Client request 2026-05-29: fixed site-wide default wall = sage green
        // (#B4BFA4). Falls back to palette[0] if the color is removed from the
        // owner-editable background-colors.json palette.
        if (this.state.backgroundColors.includes(DEFAULT_WALL_COLOR)) {
          this.state.selectedBackgroundColor = DEFAULT_WALL_COLOR;
        } else if (this.state.backgroundColors.length) {
          this.state.selectedBackgroundColor = this.state.backgroundColors[0];
        }
        this.renderColorSwatches();
        this.updateBackgroundColor();
      } catch (error) {
        console.error('Error loading background colors:', error);
        this.state.backgroundColors = [];
      }
    },

    renderColorSwatches() {
      if (!this.elements.colorSwatches) return;

      let html = '';
      this.state.backgroundColors.forEach((color, index) => {
        const isActive = color === this.state.selectedBackgroundColor;
        html += `
          <button
            type="button"
            class="color-swatch ${isActive ? 'color-swatch--active' : ''}"
            style="background-color: ${this.escapeHtml(color)}"
            data-color="${this.escapeHtml(color)}"
            aria-label="Background color ${color}"
            title="${color}"
          ></button>
        `;
      });

      this.elements.colorSwatches.innerHTML = html;
    },

    categorizeFrames() {
      this.state.categorizedFrames = {
        popular: [],
        more: [],
        floater: []
      };

      // Separate floater frames first
      const floaterFrames = [];
      const regularFrames = [];

      this.state.framesData.forEach(frame => {
        const value = frame.value.toLowerCase();
        const title = frame.title.toLowerCase();

        // Floater frames - first priority
        if (value.includes('floater') || value.includes('floating') || title.includes('floater')) {
          floaterFrames.push(frame);
        } else {
          regularFrames.push(frame);
        }
      });

      // Distribute regular frames evenly between Popular and More
      // Aim for ~24 frames each
      const targetPerTab = 24;
      const halfPoint = Math.min(targetPerTab, Math.ceil(regularFrames.length / 2));

      regularFrames.forEach((frame, index) => {
        if (index < halfPoint) {
          this.state.categorizedFrames.popular.push(frame);
        } else {
          this.state.categorizedFrames.more.push(frame);
        }
      });

      this.state.categorizedFrames.floater = floaterFrames;
    },

    renderFrameSwatches() {
      if (!this.elements.swatches) return;

      let html = '';

      // Add frames from current tab
      const framesToShow = this.state.categorizedFrames[this.state.currentTab] || [];
      framesToShow.forEach(frame => {
        html += `
          <button
            type="button"
            class="frame-swatch"
            data-frame-value="${this.escapeHtml(frame.value)}"
            data-frame-name="${this.escapeHtml(frame.title)}"
            ${frame.image_url ? `data-frame-image="${this.escapeHtml(frame.image_url)}"` : ''}
            title="${this.escapeHtml(frame.title)}"
            aria-label="${this.escapeHtml(frame.title)}"
            aria-pressed="false"
          >
            ${frame.image_url ? `
              <img
                src="${this.escapeHtml(frame.image_url)}"
                alt=""
                class="frame-swatch__image"
                loading="eager"
              >
            ` : `<span class="frame-swatch__label">${this.escapeHtml(frame.title)}</span>`}
          </button>
        `;
      });

      this.elements.swatches.innerHTML = html;
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // Frame preview positions
    FRAME_POSITIONS: ['lt', 'ct', 'rt', 'cy', 'cyr', 'lb', 'cb', 'rb'],

    // Frame types that have preview images (extracted from assets/nexo-img-*-lt.png files)
    FRAME_PREVIEW_KEYS: [
      'aluminum_floater_frame_black',
      'aluminum_floater_frame_gold',
      'aluminum_floater_frame_silver',
      'gallery_wrap_stretched_canvas',
      'high_quality_ps_frame_6163',
      'solid_wood_frame_2190_g',
      'solid_wood_frame_2619',
      'solid_wood_frame_316he',
      'solid_wood_frame_4135_g',
      'solid_wood_frame_4235_b',
      'solid_wood_frame_5035_g',
      'solid_wood_frame_5090hk',
      'solid_wood_frame_5140hk',
      'solid_wood_frame_52433',
      'solid_wood_frame_5445_16_g',
      'solid_wood_frame_5558hk',
      'solid_wood_frame_58008_1hk',
      'solid_wood_frame_5958hk',
      'solid_wood_frame_5958hk_g',
      'solid_wood_frame_5960he',
      'solid_wood_frame_5963he',
      'solid_wood_frame_6002',
      'solid_wood_frame_6831_dg',
      'solid_wood_frame_7019_101_g',
      'solid_wood_frame_7543_b',
      'solid_wood_frame_7545_db',
      'solid_wood_frame_7850_g',
      'solid_wood_frame_8038_dg',
      'solid_wood_frame_8038_ds',
      'solid_wood_frame_8260_g',
      'solid_wood_frame_8345_g',
      'solid_wood_frame_83735',
      'solid_wood_frame_83806_b',
      'solid_wood_frame_8835hb',
      'solid_wood_frame_8897_1_g',
      'solid_wood_frame_9005_h4',
      'solid_wood_frame_9334_g',
      'solid_wood_frame_9356_g',
      'solid_wood_frame_9356_s',
      'solid_wood_frame_96003',
      'solid_wood_frame_9642_ds',
      'solid_wood_frame_9660_dg',
      'solid_wood_frame_9660_ds',
      'solid_wood_frame_9660_g',
      'solid_wood_frame_9660_s',
      'solid_wood_frame_9848_g',
      'solid_wood_frame_9866_b',
      'solid_wood_frame_c_5072_g',
      'solid_wood_frame_q7737_g',
      'solid_wood_frame_s5635_3_b',
      'wood_floater_frame_black',
      'wood_floater_frame_wood_colored',
    ],

    /**
     * Convert frame value to image key
     * "Aluminum Floater Frame - Black" → "aluminum_floater_frame_black"
     * "Solid Wood Frame (2619)" → "solid_wood_frame_2619"
     */
    getFrameImageKey(frameValue) {
      if (!frameValue) return null;
      return frameValue
        .toLowerCase()
        .replace(/[()]/g, '')           // Remove parentheses
        .replace(/[-–—]/g, '_')         // Replace dashes with underscore
        .replace(/\s+/g, '_')           // Replace spaces with underscore
        .replace(/_+/g, '_')            // Collapse multiple underscores
        .replace(/^_|_$/g, '');         // Trim leading/trailing underscores
    },

    /**
     * Check if frame has preview images available
     */
    hasFramePreviewImages(frameValue) {
      const imageKey = this.getFrameImageKey(frameValue);
      const hasPreview = imageKey && this.FRAME_PREVIEW_KEYS.includes(imageKey);
      console.log('hasFramePreviewImages:', { frameValue, imageKey, hasPreview, keysLength: this.FRAME_PREVIEW_KEYS.length });
      return hasPreview;
    },

    /**
     * Get frame piece image URL
     */
    getFramePieceUrl(frameValue, position) {
      const imageKey = this.getFrameImageKey(frameValue);
      if (!imageKey) return null;

      const baseUrl = window.frameAssetsBaseUrl || '';
      const url = `${baseUrl}${imageKey}-${position}.png`;
      console.log('Frame piece URL:', { frameValue, imageKey, position, url });
      return url;
    },

    /**
     * Update frame preview with 8 pieces around artwork
     */
    updateFramePreview() {
      // Re-query frame elements in case they weren't available during init
      if (!this.elements.frameOverlay) {
        this.elements.frameOverlay = document.querySelector('[data-frame-overlay]');
      }
      if (!this.elements.framePieces || this.elements.framePieces.length === 0) {
        this.elements.framePieces = document.querySelectorAll('[data-frame-piece]');
      }
      if (!this.elements.previewWrapper) {
        this.elements.previewWrapper = document.querySelector('.frame-preview-wrapper');
      }

      if (!this.elements.frameOverlay || !this.elements.framePieces || this.elements.framePieces.length === 0) {
        return;
      }

      const frameValue = this.state.selectedFrame;
      const hasFrame = frameValue && frameValue !== '' && this.hasFramePreviewImages(frameValue);

      // Toggle overlay class for showing/hiding frame
      this.elements.frameOverlay.classList.toggle('has-frame', hasFrame);

      if (!hasFrame) {
        // Clear frame piece backgrounds when no frame
        this.elements.framePieces.forEach(piece => {
          piece.style.backgroundImage = '';
        });
        // Reset overlay positioning
        if (this.elements.frameOverlay) {
          this.elements.frameOverlay.style.top = '0';
          this.elements.frameOverlay.style.left = '0';
          this.elements.frameOverlay.style.right = '0';
          this.elements.frameOverlay.style.bottom = '0';
        }
        return;
      }

      // Load corner images to get actual dimensions (top and bottom may differ)
      const ltUrl = this.getFramePieceUrl(frameValue, 'lt');
      const lbUrl = this.getFramePieceUrl(frameValue, 'lb');

      const imgTop = new Image();
      const imgBottom = new Image();
      let topLoaded = false, bottomLoaded = false;

      const applyDimensions = () => {
        if (!topLoaded || !bottomLoaded) return;

        const topWidth = imgTop.naturalWidth;
        const topHeight = imgTop.naturalHeight;
        const bottomWidth = imgBottom.naturalWidth;
        const bottomHeight = imgBottom.naturalHeight;

        // Use max width for left/right edges, and actual heights for top/bottom
        const cornerWidth = Math.max(topWidth, bottomWidth);

        // Apply dynamic sizing via overlay positioning (extend outside wrapper)
        if (this.elements.frameOverlay) {
          this.elements.frameOverlay.style.top = `-${topHeight}px`;
          this.elements.frameOverlay.style.left = `-${cornerWidth}px`;
          this.elements.frameOverlay.style.right = `-${cornerWidth}px`;
          this.elements.frameOverlay.style.bottom = `-${bottomHeight}px`;
        }

        // Update CSS for frame pieces
        this.elements.framePieces.forEach(piece => {
          const position = piece.dataset.framePiece;

          // Set size based on position type
          if (position === 'lt' || position === 'rt') {
            // Top corners
            piece.style.width = `${topWidth}px`;
            piece.style.height = `${topHeight}px`;
          } else if (position === 'lb' || position === 'rb') {
            // Bottom corners
            piece.style.width = `${bottomWidth}px`;
            piece.style.height = `${bottomHeight}px`;
          } else if (position === 'ct') {
            // Top edge
            piece.style.height = `${topHeight}px`;
            piece.style.left = `${topWidth}px`;
            piece.style.right = `${topWidth}px`;
          } else if (position === 'cb') {
            // Bottom edge
            piece.style.height = `${bottomHeight}px`;
            piece.style.left = `${bottomWidth}px`;
            piece.style.right = `${bottomWidth}px`;
          } else if (position === 'cy' || position === 'cyr') {
            // Vertical edges
            piece.style.width = `${cornerWidth}px`;
            piece.style.top = `${topHeight}px`;
            piece.style.bottom = `${bottomHeight}px`;
          }
        });
      };

      imgTop.onload = () => { topLoaded = true; applyDimensions(); };
      imgBottom.onload = () => { bottomLoaded = true; applyDimensions(); };
      imgTop.src = ltUrl;
      imgBottom.src = lbUrl;

      // Set frame piece background images immediately
      this.elements.framePieces.forEach(piece => {
        const position = piece.dataset.framePiece;
        const url = this.getFramePieceUrl(frameValue, position);
        if (url) {
          piece.style.backgroundImage = `url('${url}')`;
        }
      });
    },

    cacheElements() {
      this.elements.selector = document.querySelector('[data-frame-selector]');
      if (!this.elements.selector) return;

      this.elements.swatches = this.elements.selector.querySelector('[data-frame-swatches]');
      this.elements.framingOptions = this.elements.selector.querySelector('[data-framing-options]');
      this.elements.previewImage = document.getElementById('frame-preview-image');
      this.elements.previewContainer = document.querySelector('[data-frame-preview-container]');
      this.elements.frameOverlay = document.querySelector('[data-frame-overlay]');
      this.elements.framePieces = document.querySelectorAll('[data-frame-piece]');
      this.elements.colorSwatches = document.querySelector('[data-color-swatches]');
      this.elements.priceDisplay = this.elements.selector.querySelector('[data-frame-price]');
      this.elements.sizeInput = document.getElementById('selected-size');
      this.elements.tabs = this.elements.selector.querySelector('[data-frame-tabs]');

      // Cart inputs
      this.elements.cartFrameStyle = this.elements.selector.querySelector('[data-cart-frame-style]');
      this.elements.cartFramingOption = this.elements.selector.querySelector('[data-cart-framing-option]');
      this.elements.cartFramePrice = this.elements.selector.querySelector('[data-cart-frame-price]');
      this.elements.cartTotalPrice = this.elements.selector.querySelector('[data-cart-total-price]');
    },

    bindEvents() {
      // Tab clicks
      this.elements.tabs?.addEventListener('click', (e) => {
        const tabButton = e.target.closest('[data-tab]');
        if (!tabButton) return;
        this.switchTab(tabButton.dataset.tab);
      });

      // Frame swatch clicks
      this.elements.swatches?.addEventListener('click', (e) => {
        const swatch = e.target.closest('.frame-swatch');
        if (!swatch) return;

        console.log('Swatch clicked:', {
          target: e.target.tagName,
          frameValue: swatch.dataset.frameValue,
          frameName: swatch.dataset.frameName,
          frameImage: swatch.dataset.frameImage,
          outerHTML: swatch.outerHTML.substring(0, 300)
        });

        this.selectFrame(swatch.dataset.frameValue, swatch.dataset.frameName);
      });

      // Framing option changes
      this.elements.framingOptions?.addEventListener('change', (e) => {
        if (e.target.name === 'framing_option') {
          this.selectFramingOption(e.target.value);
        }
      });

      // Color swatch clicks
      this.elements.colorSwatches?.addEventListener('click', (e) => {
        const swatch = e.target.closest('.color-swatch');
        if (!swatch) return;
        this.selectBackgroundColor(swatch.dataset.color);
      });

      // Listen for variant changes (size changes)
      document.addEventListener('variant:changed', (e) => {
        if (e.detail?.variant?.title) {
          this.state.selectedSize = e.detail.variant.title;
          this.state.basePrice = e.detail.variant.price;
          this.updateUI();
        }
      });
    },

    switchTab(tabName) {
      this.state.currentTab = tabName;

      // Update tab buttons active state
      this.elements.tabs?.querySelectorAll('[data-tab]').forEach(btn => {
        btn.classList.toggle('frame-tab--active', btn.dataset.tab === tabName);
      });

      // Re-render swatches
      this.renderFrameSwatches();
    },

    selectFrame(frameValue, frameName) {
      this.state.selectedFrame = frameValue;

      // Update swatch active state
      this.elements.swatches?.querySelectorAll('.frame-swatch').forEach(swatch => {
        const isActive = swatch.dataset.frameValue === frameValue;
        swatch.classList.toggle('frame-swatch--active', isActive);
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      // Show/hide framing options
      const hasFrame = frameValue && frameValue !== '';
      this.elements.framingOptions?.classList.toggle('frame-selector__section--hidden', !hasFrame);

      // Update cart input
      if (this.elements.cartFrameStyle) {
        this.elements.cartFrameStyle.value = frameName || '';
      }

      // MCA M2 Phase 09 / P05 — mirror selected name in PDP right-column summary.
      // Summary lives in main-product.liquid (rendered via mca-pdp-frame-summary
      // snippet); selector itself moved to mca-frame-selection section.
      const mcaSummaryName = document.querySelector('[data-mca-frame-summary-name]');
      if (mcaSummaryName) mcaSummaryName.textContent = frameName || 'No Frame';

      this.updateUI();
    },

    selectFramingOption(optionValue) {
      this.state.selectedFramingOption = optionValue;

      // Update cart input
      if (this.elements.cartFramingOption) {
        this.elements.cartFramingOption.value = optionValue;
      }

      this.updateUI();
    },

    selectBackgroundColor(color) {
      this.state.selectedBackgroundColor = color;

      // Update swatch active state
      this.elements.colorSwatches?.querySelectorAll('.color-swatch').forEach(swatch => {
        swatch.classList.toggle('color-swatch--active', swatch.dataset.color === color);
      });

      this.updateUI();
    },

    updateUI() {
      this.updatePreviewImage();
      this.updateFramePreview();
      this.updateBackgroundColor();
      this.updatePrice();
    },

    updateBackgroundColor() {
      if (this.elements.previewContainer) {
        this.elements.previewContainer.style.backgroundColor = this.state.selectedBackgroundColor;
      }
    },

    updatePreviewImage() {
      if (!this.elements.previewImage) return;

      const frameData = this.getSelectedFrameData();
      if (frameData?.preview_url) {
        this.elements.previewImage.src = frameData.preview_url;
      }
    },

    updatePrice() {
      const frameData = this.getSelectedFrameData();
      let totalPrice = this.state.basePrice;
      let framePrice = 0;

      if (frameData && this.state.selectedFrame) {
        // Get price based on framing option
        const optionKey = this.state.selectedFramingOption;
        if (frameData.framing_options?.[optionKey]) {
          totalPrice = frameData.framing_options[optionKey].total * 100; // Convert to cents
          framePrice = frameData.framing_options[optionKey].frame * 100;
        } else {
          framePrice = (frameData.frame_price || 0) * 100;
          totalPrice = this.state.basePrice + framePrice;
        }
      }

      // Update price display
      if (this.elements.priceDisplay) {
        this.elements.priceDisplay.textContent = this.formatMoney(totalPrice);
      }

      // Update cart inputs
      if (this.elements.cartFramePrice) {
        this.elements.cartFramePrice.value = (framePrice / 100).toFixed(2);
      }
      if (this.elements.cartTotalPrice) {
        this.elements.cartTotalPrice.value = totalPrice;
      }
    },

    getSelectedFrameData() {
      const size = this.state.selectedSize;
      const frame = this.state.selectedFrame;

      if (!size || !frame || !this.state.framePreviewData[size]) {
        return null;
      }

      return this.state.framePreviewData[size][frame] || null;
    },

    formatMoney(cents) {
      const format = window.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    },
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FrameSelector.init());
  } else {
    FrameSelector.init();
  }

  // Expose for external access
  window.FrameSelector = FrameSelector;
})();
